import { FC, useState } from 'react';
import { STATUS } from '@kontentino/kontentino-constants/Posts';
import { Alert, Button } from '@kontentino/ui';
import { usePostsContainer } from 'components/posts/context';
import Modal from 'components/shared/Modal';
import PostsSelection from 'components/workflowOptions/PostsSelection';
import PostsSelectionContainer from 'components/workflowOptions/postsSelection/PostsSelectionContainer';
import { useTranslation } from 'react-i18next';
import { ListPost } from 'types/PostDetail';
import { useUser } from 'modules/user/userSelector';
import { useMutation, useQueryClient } from 'react-query';
import PostApi from 'api/post';
import { queryKey } from 'constants/queryKey';
import { ApiClientError } from 'api/client';
import { useToast } from 'app/hooks/useToast';
import { useG2ReviewTrigger } from 'app/hooks/useG2ReviewTrigger';

type Props = {
  onFinish: () => void;
};

const MovePostsToTrashWithPostsSelection: FC<Props> = ({ onFinish }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { activeState } = usePostsContainer();
  const user = useUser();
  const { triggerG2Review } = useG2ReviewTrigger();
  const { availableStatuses } = user;

  const [step, setStep] = useState<'select' | 'confirm'>('select');
  const [selectedPosts, setSelectedPosts] = useState<ListPost[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<number[]>([]);

  const moveToTrash = useMutation(
    (postIds: number[]) => PostApi.moveMultipleToTrash(postIds),
    {
      onError: (e: ApiClientError) => {
        toast(
          e?.userMessage ??
            t('unableToMovePostsToTrash', 'Unable to move posts to trash'),
          'error',
        );
      },
      onSuccess: (data) => {
        queryClient.invalidateQueries(queryKey.trashedPosts());
        queryClient.invalidateQueries(queryKey.trashedPostsCount());
        onFinish();

        if (data.movedCount > 0) {
          triggerG2Review();
          toast(
            t('postsMovedToTrash', {
              defaultValue_one: '{{count}} post moved to trash',
              defaultValue_other: '{{count}} posts moved to trash',
              defaultValue: '{{count}} posts moved to trash',
              count: data.movedCount,
            }),
            'info',
          );
        }

        if (data.failedCount > 0) {
          toast(
            t('somePostsFailedToMoveToTrash', {
              defaultValue_one: '{{count}} post could not be moved to trash',
              defaultValue_other: '{{count}} posts could not be moved to trash',
              defaultValue: '{{count}} posts could not be moved to trash',
              count: data.failedCount,
            }),
            'warning',
          );
        }
      },
    },
  );

  const togglePost = (post: ListPost) => {
    setSelectedPosts((prev) =>
      prev.find((p) => p.id === post.id)
        ? prev.filter((p) => p.id !== post.id)
        : [...prev, post],
    );
  };

  const togglePostStatus = (status: number) => {
    setSelectedStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status],
    );
  };

  const handleSubmit = () => {
    const postIds = selectedPosts.map((post) => post.id);
    moveToTrash.mutate(postIds);
  };

  const allowedStatuses = availableStatuses(
    activeState.profiles.map((page) => page.type),
  ).filter(
    (status) =>
      !([STATUS.EXPORTED, STATUS.POSTED] as number[]).includes(status),
  );

  if (step === 'select') {
    return (
      <>
        <Modal.Header title={t('moveToTrash', 'Move to trash')} />
        <Modal.Content className="tw-pb-px">
          <PostsSelectionContainer
            allowedStatuses={allowedStatuses}
            selectedStatuses={selectedStatuses}
          >
            {(params) => (
              <PostsSelection
                allowedStatuses={allowedStatuses}
                selectedStatuses={selectedStatuses}
                onSelectPostStatus={togglePostStatus}
                posts={params.posts}
                onSelectPost={togglePost}
                selectedPosts={selectedPosts}
                onSelectAllPosts={() => setSelectedPosts(params.posts)}
                onUnselectAllPosts={() => setSelectedPosts([])}
              />
            )}
          </PostsSelectionContainer>
        </Modal.Content>
        <Modal.Footer>
          <Button variant="secondary" onClick={onFinish}>
            {t('cancel', 'Cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => setStep('confirm')}
            disabled={selectedPosts.length === 0}
          >
            {t('next', 'Next')}
          </Button>
        </Modal.Footer>
      </>
    );
  }

  return (
    <>
      <Modal.Header
        title={t('moveToTrash', 'Move to trash')}
        onGoBack={() => setStep('select')}
      />
      <Modal.Content>
        <Alert variant="warning" className="tw-mb-4">
          {t(
            'movePostsToTrashWarning',
            'Posts will be moved to trash. You can restore them later from the trash.',
          )}
        </Alert>
        <p className="tw-text-md tw-text-grayscale-140">
          {t('movePostsToTrashConfirm', {
            defaultValue_one:
              'Are you sure you want to move {{count}} post to trash?',
            defaultValue_other:
              'Are you sure you want to move {{count}} posts to trash?',
            defaultValue:
              'Are you sure you want to move {{count}} posts to trash?',
            count: selectedPosts.length,
          })}
        </p>
      </Modal.Content>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setStep('select')}>
          {t('back', 'Back')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          isLoading={moveToTrash.isLoading}
        >
          {t('moveToTrash', 'Move to trash')}
        </Button>
      </Modal.Footer>
    </>
  );
};

export default MovePostsToTrashWithPostsSelection;
